import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Slide,
  useTheme,
  alpha
} from '@mui/material';
import {
  Close as CloseIcon,
  Folder as FolderIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import FolderTree from './FolderTree';

const PANEL_WIDTH = 280;

const FolderPanelContainer = styled(Paper)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 60, // Position next to mini sidebar
  width: PANEL_WIDTH,
  height: '100vh',
  zIndex: 1300,
  borderRadius: 0,
  border: 'none',
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.background.paper, 0.95)}, 
    ${alpha(theme.palette.background.paper, 0.98)}
  )`,
  backdropFilter: 'blur(20px)',
  boxShadow: `
    8px 0 32px ${alpha(theme.palette.common.black, 0.12)},
    0 0 0 1px ${alpha(theme.palette.divider, 0.08)}
  `,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: `linear-gradient(90deg, 
      transparent 0%, 
      ${alpha(theme.palette.primary.main, 0.3)} 50%, 
      transparent 100%
    )`
  }
}));

const PanelHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2.5, 2.5, 2, 2.5),
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.primary.main, 0.03)}, 
    ${alpha(theme.palette.secondary.main, 0.02)}
  )`,
  minHeight: 72,
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 20,
    right: 20,
    height: '1px',
    background: `linear-gradient(90deg, 
      transparent 0%, 
      ${alpha(theme.palette.primary.main, 0.2)} 50%, 
      transparent 100%
    )`
  }
}));

const PanelContent = styled(Box)(({ theme }) => ({
  flex: 1,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column'
}));

const FolderPanel = ({ open, onClose, onFolderSelect, selectedFolderId }) => {
  const theme = useTheme();

  return (
    <Slide direction="right" in={open} mountOnEnter unmountOnExit>
      <FolderPanelContainer elevation={8}>
        <PanelHeader>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              p: 1,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <FolderIcon sx={{ color: 'white', fontSize: 20 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight="700" sx={{ 
                fontSize: '1.1rem',
                color: theme.palette.text.primary,
                letterSpacing: '-0.01em'
              }}>
                Folders
              </Typography>
              <Typography variant="caption" sx={{ 
                color: theme.palette.text.secondary,
                fontSize: '0.75rem'
              }}>
                Organize your content
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: theme.palette.text.secondary,
              borderRadius: 2,
              p: 1,
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                backgroundColor: alpha(theme.palette.error.main, 0.1),
                color: theme.palette.error.main,
                transform: 'scale(1.05)'
              }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </PanelHeader>
        
        <PanelContent>
          <FolderTree 
            onFolderSelect={onFolderSelect}
            selectedFolderId={selectedFolderId}
          />
        </PanelContent>
      </FolderPanelContainer>
    </Slide>
  );
};

export default FolderPanel;
